import { Component, Inject, OnInit, OnDestroy, AfterViewInit, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MaterialModule } from '../../material/material.module';
import { DataService } from '../../middle-school-math/services/data';
import { SecurityService } from '../../security/security.service';
import { Book, Chapter } from '../../middle-school-math/models';

declare const stripe: any;
declare const elements: any;

export interface SubscriptionPaywallDialogData {
  book: Book;
  chapter?: Chapter;
  suggestedPlan?: 'MONTHLY' | 'ANNUAL';
}

@Component({
  selector: 'app-subscription-paywall-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, MatDialogModule, MaterialModule],
  template: `
    <div class="paywall-modal">
      <!-- Top Hero Header -->
      <div class="paywall-header">
        <button mat-icon-button class="close-btn" (click)="closeDialog(false)">
          <mat-icon>close</mat-icon>
        </button>
        <div class="header-content">
          <div class="premium-badge">
            <mat-icon class="badge-icon">workspace_premium</mat-icon> PREMIUM ACCESS
          </div>
          <h2 class="paywall-title">Unlock Full Access to {{ data.book?.title || 'This Course' }}</h2>
          <p class="paywall-subtitle">
            Get unlimited access to all interactive audio lessons, flashcard decks, and chapter quizzes.
          </p>
        </div>
      </div>

      <!-- Main Body -->
      <div class="paywall-body" *ngIf="!paymentSuccess">
        <!-- Locked Chapter Alert if opened from a specific chapter -->
        <div class="locked-alert" *ngIf="data.chapter">
          <mat-icon class="locked-icon">lock</mat-icon>
          <div class="locked-text">
            <strong>{{ data.chapter.title }}</strong> is a subscriber-only chapter.
            <span *ngIf="data.chapter.isFreePreview === false">Choose a plan below to unlock all chapters.</span>
          </div>
        </div>

        <!-- Plan Selection -->
        <div class="section-title">1. Choose Your Subscription Plan</div>
        <div class="plans-grid">
          <!-- Monthly Plan -->
          <div class="plan-card" 
               [class.selected]="selectedPlan === 'MONTHLY'"
               (click)="selectPlan('MONTHLY')">
            <div class="plan-radio-row">
              <div class="custom-radio" [class.checked]="selectedPlan === 'MONTHLY'"></div>
              <span class="plan-name">Monthly</span>
            </div>
            <div class="plan-price">
              <span class="currency-symbol">$</span>
              <span class="price-amount">{{ monthlyPrice }}</span>
              <span class="price-period">/ month</span>
            </div>
            <div class="plan-tagline">Flexible, cancel anytime</div>
          </div>

          <!-- Annual Plan (Featured) -->
          <div class="plan-card annual-featured" 
               [class.selected]="selectedPlan === 'ANNUAL'"
               (click)="selectPlan('ANNUAL')">
            <div class="save-badge">SAVE {{ savingsPercentage }}%</div>
            <div class="plan-radio-row">
              <div class="custom-radio" [class.checked]="selectedPlan === 'ANNUAL'"></div>
              <span class="plan-name">Annual</span>
              <span class="best-value-pill">Best Value</span>
            </div>
            <div class="plan-price">
              <span class="currency-symbol">$</span>
              <span class="price-amount">{{ annualPrice }}</span>
              <span class="price-period">/ year</span>
            </div>
            <div class="plan-tagline">Just $ {{ annualMonthlyEquivalent }}/month billed annually</div>
          </div>
        </div>

        <!-- Value Proposition Perks -->
        <div class="perks-list">
          <div class="perk-item">
            <mat-icon class="perk-icon">check_circle</mat-icon>
            <span><strong>Full Book Access:</strong> Unlock all {{ totalChapters }} chapters immediately</span>
          </div>
          <div class="perk-item">
            <mat-icon class="perk-icon">check_circle</mat-icon>
            <span><strong>Interactive Audio Lessons:</strong> High-definition narrated slides</span>
          </div>
          <div class="perk-item">
            <mat-icon class="perk-icon">check_circle</mat-icon>
            <span><strong>Flashcards & Quizzes:</strong> Instant self-assessment & mastery metrics</span>
          </div>
          <div class="perk-item">
            <mat-icon class="perk-icon">check_circle</mat-icon>
            <span><strong>Instant Activation:</strong> Safe 256-bit encrypted checkout with Stripe</span>
          </div>
        </div>

        <!-- Account Section: Unified Registration & Checkout (Option A) -->
        <div class="checkout-box">
          <div class="section-title">2. Account &amp; Access Details</div>

          <!-- State A: Already Logged In -->
          <div *ngIf="isLoggedIn" class="logged-in-badge-card">
            <div class="user-avatar-circle">
              <mat-icon>account_circle</mat-icon>
            </div>
            <div class="logged-in-info">
              <div class="logged-in-label">Subscribing as:</div>
              <div class="logged-in-user">
                <strong>{{ loggedInUserName || userEmail }}</strong> 
                <span class="logged-in-email" *ngIf="loggedInUserName && loggedInUserName !== userEmail">({{ userEmail }})</span>
              </div>
            </div>
            <button type="button" class="switch-account-btn" (click)="switchAccount()" [disabled]="isProcessing">
              Switch Account
            </button>
          </div>

          <!-- State B: Visitor (Guest / Not Logged In) -->
          <div *ngIf="!isLoggedIn" class="visitor-auth-container">
            <!-- Tabs: Register vs Sign In -->
            <div class="auth-tabs-header">
              <button type="button" 
                      class="auth-tab-btn" 
                      [class.active]="authTab === 'REGISTER'"
                      (click)="setAuthTab('REGISTER')">
                <mat-icon class="tab-icon">person_add</mat-icon>
                <span>Create Account &amp; Subscribe</span>
              </button>
              <button type="button" 
                      class="auth-tab-btn" 
                      [class.active]="authTab === 'LOGIN'"
                      (click)="setAuthTab('LOGIN')">
                <mat-icon class="tab-icon">login</mat-icon>
                <span>Existing Member? Sign In</span>
              </button>
            </div>

            <!-- Tab 1: 1-Step Unified Registration Fields -->
            <div *ngIf="authTab === 'REGISTER'" class="auth-tab-content">
              <p class="auth-tab-hint">
                <mat-icon class="hint-icon">info</mat-icon>
                Create your account below. Your subscription will be linked immediately so you can log in from any device.
              </p>

              <div class="form-row-2col">
                <div class="form-group">
                  <label class="form-label">First Name <span class="req">*</span></label>
                  <input type="text" 
                         [(ngModel)]="firstName" 
                         placeholder="First Name" 
                         class="app-input" 
                         [disabled]="isProcessing" />
                </div>
                <div class="form-group">
                  <label class="form-label">Last Name</label>
                  <input type="text" 
                         [(ngModel)]="lastName" 
                         placeholder="Last Name" 
                         class="app-input" 
                         [disabled]="isProcessing" />
                </div>
              </div>

              <div class="form-group">
                <label class="form-label">Email Address (Your Username) <span class="req">*</span></label>
                <input type="email" 
                       [(ngModel)]="userEmail" 
                       placeholder="you@example.com" 
                       class="app-input" 
                       [disabled]="isProcessing" />
              </div>

              <div class="form-group">
                <label class="form-label">Create Password <span class="req">*</span></label>
                <div class="password-input-wrapper">
                  <input [type]="showPassword ? 'text' : 'password'" 
                         [(ngModel)]="password" 
                         placeholder="At least 6 characters" 
                         class="app-input password-field" 
                         [disabled]="isProcessing" />
                  <button type="button" class="pwd-toggle-btn" (click)="togglePasswordVisibility()" tabindex="-1">
                    <mat-icon>{{ showPassword ? 'visibility_off' : 'visibility' }}</mat-icon>
                  </button>
                </div>
              </div>
            </div>

            <!-- Tab 2: Existing Member Sign In -->
            <div *ngIf="authTab === 'LOGIN'" class="auth-tab-content login-tab-box">
              <p class="auth-tab-hint">
                <mat-icon class="hint-icon">lock_open</mat-icon>
                Sign in with your existing account credentials to continue checkout.
              </p>

              <div class="form-group">
                <label class="form-label">Email Address</label>
                <input type="email" 
                       [(ngModel)]="userEmail" 
                       placeholder="you@example.com" 
                       class="app-input" 
                       [disabled]="isLoggingIn || isProcessing" />
              </div>

              <div class="form-group">
                <label class="form-label">Password</label>
                <div class="password-input-wrapper">
                  <input [type]="showPassword ? 'text' : 'password'" 
                         [(ngModel)]="loginPassword" 
                         placeholder="Your password" 
                         class="app-input password-field" 
                         [disabled]="isLoggingIn || isProcessing"
                         (keyup.enter)="inlineSignIn()" />
                  <button type="button" class="pwd-toggle-btn" (click)="togglePasswordVisibility()" tabindex="-1">
                    <mat-icon>{{ showPassword ? 'visibility_off' : 'visibility' }}</mat-icon>
                  </button>
                </div>
              </div>

              <div *ngIf="loginError" class="auth-error-banner">
                <mat-icon>error_outline</mat-icon> {{ loginError }}
              </div>

              <button type="button" 
                      class="inline-signin-btn" 
                      (click)="inlineSignIn()" 
                      [disabled]="isLoggingIn || !userEmail || !loginPassword">
                <span *ngIf="!isLoggingIn">
                  <mat-icon class="btn-icon">login</mat-icon> Sign In &amp; Continue
                </span>
                <span *ngIf="isLoggingIn" class="btn-loading">
                  <mat-spinner diameter="18" class="btn-spinner"></mat-spinner> Signing in...
                </span>
              </button>
            </div>
          </div>

          <!-- Section 3: Payment Details -->
          <div class="payment-section" *ngIf="isLoggedIn || authTab === 'REGISTER'">
            <div class="section-title payment-step-title">3. Payment Information</div>

            <!-- Stripe Card Element -->
            <div class="form-group">
              <label class="form-label">Credit or Debit Card</label>
              <div #cardInfo class="stripe-card-wrapper" [class.processing]="isProcessing"></div>
              <div *ngIf="cardError" class="card-error-text">
                <mat-icon>error_outline</mat-icon> {{ cardError }}
              </div>
            </div>

            <!-- General Error Banner -->
            <div *ngIf="generalError" class="general-error-banner">
              <mat-icon>error</mat-icon> {{ generalError }}
            </div>

            <!-- Checkout Button -->
            <button class="subscribe-btn" 
                    [disabled]="isProcessing || !userEmail || (!isLoggedIn && (!firstName || !password))"
                    (click)="submitSubscription()">
              <span *ngIf="!isProcessing">
                <mat-icon class="btn-lock-icon">lock</mat-icon>
                {{ isLoggedIn ? 'Subscribe Now' : 'Create Account & Subscribe' }} &bull; $ {{ currentPlanPrice }} / {{ selectedPlan === 'MONTHLY' ? 'mo' : 'yr' }}
              </span>
              <span *ngIf="isProcessing" class="btn-loading">
                <mat-spinner diameter="20" class="btn-spinner"></mat-spinner>
                Processing Secure Payment...
              </span>
            </button>

            <!-- Security Badge Footer -->
            <div class="security-footer">
              <mat-icon class="sec-icon">verified_user</mat-icon>
              <span>256-bit encrypted checkout powered by Stripe. Cancel anytime in your profile.</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Success Screen -->
      <div class="paywall-success" *ngIf="paymentSuccess">
        <div class="success-icon-wrap">
          <mat-icon>check_circle</mat-icon>
        </div>
        <h2 class="success-title">Subscription Activated! 🎉</h2>
        <p class="success-desc">
          Thank you for subscribing to <strong>{{ data.book?.title }}</strong>. Full access to all chapters, interactive lessons, flashcards, and quizzes is now active.
        </p>

        <div class="success-sub-details">
          <div class="detail-row" *ngIf="registeredUserName">
            <span class="detail-label">Name:</span>
            <span class="detail-value">{{ registeredUserName }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Account (Login):</span>
            <span class="detail-value">{{ userEmail }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Plan:</span>
            <span class="detail-value">{{ selectedPlan === 'MONTHLY' ? 'Monthly ($' + monthlyPrice + '/mo)' : 'Annual ($' + annualPrice + '/yr)' }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Status:</span>
            <span class="detail-value active-status">Active</span>
          </div>
        </div>

        <div class="success-login-notice" *ngIf="!isLoggedInInitially">
          <mat-icon class="notice-icon">stars</mat-icon>
          <span>You are now logged in! Use your email (<strong>{{ userEmail }}</strong>) and chosen password whenever you sign in.</span>
        </div>

        <button mat-raised-button color="primary" class="continue-btn" (click)="closeDialog(true)">
          Start Learning Now
        </button>
      </div>
    </div>
  `,
  styles: [`
    .paywall-modal {
      display: flex;
      flex-direction: column;
      max-height: 90vh;
      overflow-y: auto;
      font-family: inherit;
    }

    .paywall-header {
      background: linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #4338ca 100%);
      color: white;
      padding: 2rem 2rem 1.75rem 2rem;
      position: relative;
      border-top-left-radius: 8px;
      border-top-right-radius: 8px;
    }
    .close-btn {
      position: absolute;
      top: 12px;
      right: 12px;
      color: #c7d2fe;
    }
    .close-btn:hover { color: white; }

    .header-content {
      max-width: 600px;
    }
    .premium-badge {
      display: inline-flex;
      align-items: center;
      gap: 0.35rem;
      background: rgba(251, 191, 36, 0.2);
      border: 1px solid rgba(251, 191, 36, 0.4);
      color: #fde047;
      font-size: 0.75rem;
      font-weight: 800;
      letter-spacing: 0.08em;
      padding: 0.25rem 0.75rem;
      border-radius: 20px;
      margin-bottom: 0.75rem;
    }
    .badge-icon { font-size: 16px; height: 16px; width: 16px; }

    .paywall-title {
      font-size: 1.6rem;
      font-weight: 800;
      line-height: 1.25;
      margin: 0 0 0.5rem 0;
      color: #ffffff;
    }
    .paywall-subtitle {
      font-size: 0.92rem;
      color: #c7d2fe;
      margin: 0;
      line-height: 1.45;
    }

    .paywall-body {
      padding: 1.5rem 2rem 2rem 2rem;
      background: #f8fafc;
    }

    .locked-alert {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      background: #fef3c7;
      border: 1px solid #fde68a;
      color: #92400e;
      padding: 0.75rem 1rem;
      border-radius: 8px;
      font-size: 0.88rem;
      margin-bottom: 1.25rem;
    }
    .locked-icon { color: #d97706; }

    .section-title {
      font-size: 0.95rem;
      font-weight: 700;
      color: #334155;
      margin-bottom: 0.75rem;
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }

    .payment-step-title {
      margin-top: 1.5rem;
      border-top: 1px solid #e2e8f0;
      padding-top: 1.25rem;
    }

    .plans-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 1rem;
      margin-bottom: 1.25rem;
    }
    @media (max-width: 580px) {
      .plans-grid { grid-template-columns: 1fr; }
    }

    .plan-card {
      background: white;
      border: 2px solid #e2e8f0;
      border-radius: 12px;
      padding: 1.25rem;
      cursor: pointer;
      position: relative;
      transition: all 0.2s ease-in-out;
      display: flex;
      flex-direction: column;
    }
    .plan-card:hover {
      border-color: #a5b4fc;
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(99, 102, 241, 0.08);
    }
    .plan-card.selected {
      border-color: #4f46e5;
      background: #eef2ff;
      box-shadow: 0 4px 16px rgba(79, 70, 229, 0.15);
    }

    .annual-featured {
      border-color: #818cf8;
    }
    .save-badge {
      position: absolute;
      top: -10px;
      right: 14px;
      background: #16a34a;
      color: white;
      font-size: 0.7rem;
      font-weight: 800;
      letter-spacing: 0.05em;
      padding: 0.2rem 0.6rem;
      border-radius: 12px;
      box-shadow: 0 2px 6px rgba(22, 163, 74, 0.3);
    }

    .plan-radio-row {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin-bottom: 0.5rem;
    }
    .custom-radio {
      width: 18px;
      height: 18px;
      border-radius: 50%;
      border: 2px solid #cbd5e1;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s;
    }
    .custom-radio.checked {
      border-color: #4f46e5;
      background: #4f46e5;
    }
    .custom-radio.checked::after {
      content: '';
      width: 6px;
      height: 6px;
      background: white;
      border-radius: 50%;
    }
    .plan-name {
      font-weight: 700;
      color: #1e293b;
      font-size: 1.05rem;
    }
    .best-value-pill {
      font-size: 0.7rem;
      font-weight: 700;
      color: #4338ca;
      background: #e0e7ff;
      padding: 0.15rem 0.5rem;
      border-radius: 10px;
    }

    .plan-price {
      display: flex;
      align-items: baseline;
      gap: 0.15rem;
      margin-bottom: 0.25rem;
    }
    .currency-symbol {
      font-size: 1.2rem;
      font-weight: 700;
      color: #1e293b;
    }
    .price-amount {
      font-size: 1.85rem;
      font-weight: 800;
      color: #1e293b;
      line-height: 1;
    }
    .price-period {
      font-size: 0.85rem;
      color: #64748b;
      font-weight: 500;
      margin-left: 0.2rem;
    }
    .plan-tagline {
      font-size: 0.78rem;
      color: #64748b;
      margin-top: 0.25rem;
    }

    .perks-list {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 0.6rem 1rem;
      background: white;
      border: 1px solid #e2e8f0;
      padding: 0.9rem 1.25rem;
      border-radius: 10px;
      margin-bottom: 1.25rem;
    }
    @media (max-width: 580px) {
      .perks-list { grid-template-columns: 1fr; }
    }
    .perk-item {
      display: flex;
      align-items: flex-start;
      gap: 0.5rem;
      font-size: 0.84rem;
      color: #334155;
      line-height: 1.35;
    }
    .perk-icon {
      font-size: 18px;
      height: 18px;
      width: 18px;
      color: #10b981;
      flex-shrink: 0;
      margin-top: 1px;
    }

    .checkout-box {
      background: white;
      border: 1px solid #cbd5e1;
      border-radius: 12px;
      padding: 1.35rem 1.5rem;
    }

    /* Logged In State Badge */
    .logged-in-badge-card {
      display: flex;
      align-items: center;
      gap: 1rem;
      background: #eff6ff;
      border: 1px solid #bfdbfe;
      border-radius: 10px;
      padding: 0.85rem 1.15rem;
      margin-bottom: 1rem;
    }
    .user-avatar-circle {
      color: #2563eb;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .user-avatar-circle mat-icon {
      font-size: 36px;
      height: 36px;
      width: 36px;
    }
    .logged-in-info {
      flex: 1;
    }
    .logged-in-label {
      font-size: 0.75rem;
      color: #64748b;
      text-transform: uppercase;
      font-weight: 600;
      letter-spacing: 0.03em;
    }
    .logged-in-user {
      font-size: 0.95rem;
      color: #1e293b;
    }
    .logged-in-email {
      color: #64748b;
      font-size: 0.88rem;
    }
    .switch-account-btn {
      background: none;
      border: 1px solid #93c5fd;
      color: #1d4ed8;
      border-radius: 6px;
      padding: 0.35rem 0.75rem;
      font-size: 0.8rem;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s;
    }
    .switch-account-btn:hover:not(:disabled) {
      background: #dbeafe;
    }

    /* Visitor Auth Tabs */
    .visitor-auth-container {
      margin-bottom: 1rem;
    }
    .auth-tabs-header {
      display: flex;
      gap: 0.5rem;
      background: #f1f5f9;
      padding: 0.35rem;
      border-radius: 10px;
      margin-bottom: 1.2rem;
    }
    .auth-tab-btn {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.4rem;
      background: none;
      border: none;
      padding: 0.6rem 0.8rem;
      border-radius: 8px;
      font-size: 0.86rem;
      font-weight: 600;
      color: #64748b;
      cursor: pointer;
      transition: all 0.2s;
    }
    .auth-tab-btn.active {
      background: white;
      color: #4338ca;
      box-shadow: 0 2px 6px rgba(0,0,0,0.06);
      font-weight: 700;
    }
    .tab-icon {
      font-size: 18px;
      height: 18px;
      width: 18px;
    }

    .auth-tab-hint {
      display: flex;
      align-items: center;
      gap: 0.4rem;
      font-size: 0.82rem;
      color: #475569;
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      border-radius: 6px;
      padding: 0.5rem 0.75rem;
      margin-bottom: 1rem;
    }
    .hint-icon {
      font-size: 16px;
      height: 16px;
      width: 16px;
      color: #6366f1;
      flex-shrink: 0;
    }

    .form-row-2col {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 0.85rem;
    }
    @media (max-width: 500px) {
      .form-row-2col { grid-template-columns: 1fr; }
    }

    .form-group {
      margin-bottom: 0.95rem;
    }
    .form-label {
      display: block;
      font-size: 0.82rem;
      font-weight: 600;
      color: #475569;
      margin-bottom: 0.35rem;
    }
    .req {
      color: #e11d48;
    }

    .app-input {
      width: 100%;
      box-sizing: border-box;
      padding: 0.65rem 0.85rem;
      border: 1.5px solid #cbd5e1;
      border-radius: 8px;
      font-size: 0.92rem;
      outline: none;
      transition: border-color 0.2s;
      background: #fff;
    }
    .app-input:focus {
      border-color: #4f46e5;
    }

    .password-input-wrapper {
      position: relative;
      display: flex;
      align-items: center;
    }
    .password-field {
      padding-right: 2.5rem;
    }
    .pwd-toggle-btn {
      position: absolute;
      right: 6px;
      background: none;
      border: none;
      color: #64748b;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 4px;
    }
    .pwd-toggle-btn mat-icon {
      font-size: 20px;
      height: 20px;
      width: 20px;
    }
    .pwd-toggle-btn:hover {
      color: #334155;
    }

    .login-tab-box {
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      border-radius: 10px;
      padding: 1.15rem;
    }
    .inline-signin-btn {
      width: 100%;
      background: #4338ca;
      color: white;
      border: none;
      border-radius: 8px;
      padding: 0.75rem 1rem;
      font-size: 0.95rem;
      font-weight: 700;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.4rem;
      transition: all 0.2s;
      margin-top: 0.75rem;
    }
    .inline-signin-btn:hover:not(:disabled) {
      background: #3730a3;
    }
    .inline-signin-btn:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
    .btn-icon {
      font-size: 18px;
      height: 18px;
      width: 18px;
    }

    .auth-error-banner {
      display: flex;
      align-items: center;
      gap: 0.4rem;
      background: #ffe4e6;
      border: 1px solid #fecdd3;
      color: #be123c;
      padding: 0.5rem 0.75rem;
      border-radius: 6px;
      font-size: 0.82rem;
      margin-top: 0.5rem;
    }
    .auth-error-banner mat-icon {
      font-size: 16px;
      height: 16px;
      width: 16px;
      flex-shrink: 0;
    }

    /* Stripe Card Wrapper */
    .stripe-card-wrapper {
      padding: 0.75rem 0.85rem;
      border: 1.5px solid #cbd5e1;
      border-radius: 8px;
      background: #fafafa;
      transition: border-color 0.2s;
    }
    .stripe-card-wrapper.processing {
      opacity: 0.6;
      pointer-events: none;
    }
    .card-error-text {
      color: #e11d48;
      font-size: 0.8rem;
      margin-top: 0.35rem;
      display: flex;
      align-items: center;
      gap: 0.3rem;
    }
    .card-error-text mat-icon { font-size: 16px; height: 16px; width: 16px; }

    .general-error-banner {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      background: #ffe4e6;
      border: 1px solid #fecdd3;
      color: #be123c;
      padding: 0.65rem 0.85rem;
      border-radius: 8px;
      font-size: 0.85rem;
      margin-bottom: 1rem;
    }

    .subscribe-btn {
      width: 100%;
      background: linear-gradient(135deg, #4f46e5 0%, #4338ca 100%);
      color: white;
      border: none;
      border-radius: 8px;
      padding: 0.85rem 1.25rem;
      font-size: 1.05rem;
      font-weight: 700;
      cursor: pointer;
      transition: all 0.2s;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 4px 12px rgba(79, 70, 229, 0.25);
    }
    .subscribe-btn:hover:not(:disabled) {
      background: linear-gradient(135deg, #4338ca 0%, #3730a3 100%);
      box-shadow: 0 6px 16px rgba(79, 70, 229, 0.35);
      transform: translateY(-1px);
    }
    .subscribe-btn:disabled {
      opacity: 0.6;
      cursor: not-allowed;
      box-shadow: none;
    }
    .btn-lock-icon {
      font-size: 18px;
      height: 18px;
      width: 18px;
      vertical-align: middle;
      margin-right: 0.4rem;
    }
    .btn-loading {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }
    .btn-spinner {
      display: inline-block;
    }

    .security-footer {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.4rem;
      margin-top: 0.85rem;
      font-size: 0.76rem;
      color: #64748b;
      text-align: center;
    }
    .sec-icon { font-size: 16px; height: 16px; width: 16px; color: #10b981; }

    /* Success Screen */
    .paywall-success {
      padding: 3rem 2rem;
      text-align: center;
      background: white;
      border-radius: 8px;
    }
    .success-icon-wrap {
      width: 72px;
      height: 72px;
      background: #ecfdf5;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 1.5rem auto;
      color: #10b981;
    }
    .success-icon-wrap mat-icon { font-size: 48px; height: 48px; width: 48px; }
    .success-title {
      font-size: 1.6rem;
      font-weight: 800;
      color: #0f172a;
      margin: 0 0 0.75rem 0;
    }
    .success-desc {
      color: #475569;
      font-size: 0.95rem;
      max-width: 480px;
      margin: 0 auto 1.5rem auto;
      line-height: 1.5;
    }
    .success-sub-details {
      max-width: 380px;
      margin: 0 auto 1.25rem auto;
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      padding: 0.75rem 1.15rem;
      text-align: left;
    }
    .detail-row {
      display: flex;
      justify-content: space-between;
      font-size: 0.85rem;
      padding: 0.35rem 0;
    }
    .detail-label { color: #64748b; }
    .detail-value { font-weight: 600; color: #1e293b; }
    .active-status { color: #16a34a; font-weight: 700; }

    .success-login-notice {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      max-width: 440px;
      margin: 0 auto 1.75rem auto;
      background: #f0fdf4;
      border: 1px solid #bbf7d0;
      color: #166534;
      padding: 0.65rem 1rem;
      border-radius: 8px;
      font-size: 0.85rem;
      text-align: left;
    }
    .notice-icon { color: #16a34a; font-size: 20px; height: 20px; width: 20px; flex-shrink: 0; }

    .continue-btn {
      min-width: 220px;
      padding: 0.65rem 1.75rem;
      font-weight: 700;
      font-size: 1rem;
    }
  `]
})
export class SubscriptionPaywallDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('cardInfo') cardInfoElement!: ElementRef;

  selectedPlan: 'MONTHLY' | 'ANNUAL' = 'ANNUAL';
  monthlyPrice: number = 9.99;
  annualPrice: number = 79.99;
  currency: string = 'USD';
  savingsPercentage: number = 33;

  // Authentication & Registration Form Fields
  isLoggedIn: boolean = false;
  isLoggedInInitially: boolean = false;
  authTab: 'REGISTER' | 'LOGIN' = 'REGISTER';
  firstName: string = '';
  lastName: string = '';
  userEmail: string = '';
  password: string = '';
  showPassword: boolean = false;

  // Inline Sign In Fields
  loginPassword: string = '';
  loginError: string | null = null;
  isLoggingIn: boolean = false;
  loggedInUserName: string = '';
  registeredUserName: string = '';

  totalChapters: number = 24;

  card: any;
  cardHandler = this.onCardChange.bind(this);
  cardError: string | null = null;
  generalError: string | null = null;
  isProcessing: boolean = false;
  paymentSuccess: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<SubscriptionPaywallDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SubscriptionPaywallDialogData,
    private dataService: DataService,
    private securityService: SecurityService,
    private cd: ChangeDetectorRef
  ) {}

  ngOnInit() {
    if (this.data.suggestedPlan) {
      this.selectedPlan = this.data.suggestedPlan;
    }
    if (this.data.book) {
      if (this.data.book.monthlyPrice) this.monthlyPrice = this.data.book.monthlyPrice;
      if (this.data.book.annualPrice) this.annualPrice = this.data.book.annualPrice;
      if (this.data.book.currency) this.currency = this.data.book.currency;
    }
    this.calculateSavings();

    // Check if user is already authenticated
    this.isLoggedIn = this.securityService.loggedIn();
    this.isLoggedInInitially = this.isLoggedIn;

    if (this.isLoggedIn) {
      this.userEmail = this.dataService.getCurrentUserEmail() || localStorage.getItem('User') || '';
      this.extractUserName();
    } else {
      this.userEmail = this.dataService.getCurrentUserEmail() || '';
      this.authTab = 'REGISTER';
    }

    // Fetch plan details from server
    if (this.data.book?.id) {
      this.dataService.getSubscriptionPlan(this.data.book.id).subscribe({
        next: (plan) => {
          if (plan) {
            if (plan.monthlyPrice) this.monthlyPrice = plan.monthlyPrice;
            if (plan.annualPrice) this.annualPrice = plan.annualPrice;
            if (plan.currency) this.currency = plan.currency;
            this.calculateSavings();
            this.cd.detectChanges();
          }
        },
        error: (err) => console.warn('Could not load plan from server:', err)
      });

      this.dataService.getChapters(this.data.book.id).subscribe({
        next: (chaps) => {
          if (chaps && chaps.length > 0) this.totalChapters = chaps.length;
        }
      });
    }
  }

  extractUserName() {
    try {
      const authUserStr = localStorage.getItem('AuthUser');
      if (authUserStr) {
        const authUser = JSON.parse(authUserStr);
        if (authUser.firstName) {
          this.loggedInUserName = `${authUser.firstName} ${authUser.lastName || ''}`.trim();
        }
      }
    } catch (e) {
      console.warn('Could not parse AuthUser:', e);
    }
  }

  calculateSavings() {
    const monthlyTotal = this.monthlyPrice * 12;
    if (monthlyTotal > 0 && this.annualPrice < monthlyTotal) {
      this.savingsPercentage = Math.round(((monthlyTotal - this.annualPrice) / monthlyTotal) * 100);
    } else {
      this.savingsPercentage = 30;
    }
  }

  get currentPlanPrice(): number {
    return this.selectedPlan === 'MONTHLY' ? this.monthlyPrice : this.annualPrice;
  }

  get annualMonthlyEquivalent(): string {
    return (this.annualPrice / 12).toFixed(2);
  }

  selectPlan(plan: 'MONTHLY' | 'ANNUAL') {
    this.selectedPlan = plan;
  }

  setAuthTab(tab: 'REGISTER' | 'LOGIN') {
    this.authTab = tab;
    this.loginError = null;
    this.generalError = null;
    if (tab === 'REGISTER') {
      setTimeout(() => {
        if (!this.card && this.cardInfoElement) {
          this.initStripeCard();
        }
      }, 100);
    }
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  switchAccount() {
    localStorage.removeItem('AuthToken');
    localStorage.removeItem('User');
    localStorage.removeItem('AuthUser');
    this.securityService.updateLoginStatus();
    this.isLoggedIn = false;
    this.isLoggedInInitially = false;
    this.userEmail = '';
    this.password = '';
    this.firstName = '';
    this.lastName = '';
    this.authTab = 'REGISTER';
    this.cd.detectChanges();
  }

  inlineSignIn() {
    if (!this.userEmail || !this.userEmail.includes('@')) {
      this.loginError = 'Please enter a valid email address.';
      return;
    }
    if (!this.loginPassword) {
      this.loginError = 'Please enter your password.';
      return;
    }

    this.isLoggingIn = true;
    this.loginError = null;

    const credentials = {
      loginId: this.userEmail.trim(),
      password: this.loginPassword
    };

    this.securityService.authenticateUser(credentials).subscribe({
      next: (res: any) => {
        this.isLoggingIn = false;
        if (res && res.jwt) {
          localStorage.setItem('AuthToken', res.jwt);
          localStorage.setItem('User', this.userEmail.trim());

          this.isLoggedIn = true;
          this.isLoggedInInitially = true;
          this.loggedInUserName = this.userEmail.trim();

          // Fetch profile to get full name
          this.securityService.getCustomerProfileByLoginId(this.userEmail.trim()).subscribe({
            next: (prof: any) => {
              if (prof && prof.firstName) {
                this.loggedInUserName = `${prof.firstName} ${prof.lastName || ''}`.trim();
                localStorage.setItem('AuthUser', JSON.stringify({
                  loginId: this.userEmail.trim(),
                  email: this.userEmail.trim(),
                  firstName: prof.firstName,
                  lastName: prof.lastName
                }));
              }
              this.cd.detectChanges();
            },
            error: () => this.cd.detectChanges()
          });

          this.securityService.updateLoginStatus();
          this.loginPassword = '';
          this.cd.detectChanges();

          // Ensure Stripe card element is mounted
          setTimeout(() => {
            if (!this.card && this.cardInfoElement) {
              this.initStripeCard();
            }
          }, 100);
        } else {
          this.loginError = 'Authentication failed. Please check your credentials.';
          this.cd.detectChanges();
        }
      },
      error: (err: any) => {
        this.isLoggingIn = false;
        console.error('Sign in error:', err);
        this.loginError = err.error?.message || 'Invalid email or password. Please try again.';
        this.cd.detectChanges();
      }
    });
  }

  ngAfterViewInit() {
    // Mount Stripe Elements Card
    setTimeout(() => {
      this.initStripeCard();
    }, 150);
  }

  ngOnDestroy() {
    if (this.card) {
      this.card.removeEventListener('change', this.cardHandler);
      this.card.destroy();
    }
  }

  initStripeCard() {
    if (typeof elements === 'undefined' || !this.cardInfoElement) {
      console.warn('Stripe elements not loaded or element ref missing');
      return;
    }

    if (this.card) {
      return;
    }

    const cardStyle = {
      base: {
        color: '#1e293b',
        fontFamily: 'inherit',
        fontSmoothing: 'antialiased',
        fontSize: '16px',
        '::placeholder': {
          color: '#94a3b8'
        }
      },
      invalid: {
        color: '#e11d48',
        iconColor: '#e11d48'
      }
    };

    this.card = elements.create('card', { style: cardStyle });
    this.card.mount(this.cardInfoElement.nativeElement);
    this.card.addEventListener('change', this.cardHandler);
  }

  onCardChange({ error }: any) {
    if (error) {
      this.cardError = error.message;
    } else {
      this.cardError = null;
    }
    this.cd.detectChanges();
  }

  async submitSubscription() {
    if (!this.userEmail || !this.userEmail.includes('@')) {
      this.generalError = 'Please provide a valid email address.';
      return;
    }

    if (!this.isLoggedIn) {
      if (!this.firstName || !this.firstName.trim()) {
        this.generalError = 'Please enter your first name.';
        return;
      }
      if (!this.password || this.password.length < 6) {
        this.generalError = 'Password must be at least 6 characters.';
        return;
      }
    }

    if (!this.data.book?.id) {
      this.generalError = 'Missing book information.';
      return;
    }

    this.isProcessing = true;
    this.generalError = null;
    this.cardError = null;

    try {
      // 1. Request PaymentIntent from backend
      this.dataService.createSubscriptionPaymentIntent(this.data.book.id, this.selectedPlan, this.userEmail).subscribe({
        next: async (res: any) => {
          if (!res || !res.clientSecret) {
            this.generalError = 'Failed to initiate payment. Please try again.';
            this.isProcessing = false;
            this.cd.detectChanges();
            return;
          }

          const clientSecret = res.clientSecret;

          // 2. If running in simulated local test mode or without live Stripe keys:
          if (res.isSimulated || clientSecret.startsWith('pi_simulated_') || typeof stripe === 'undefined') {
            const paymentIntentId = res.paymentIntentId || ('pi_simulated_' + Date.now());
            // Confirm subscription activation & user registration in backend
            this.dataService.confirmSubscription(
              paymentIntentId,
              this.data.book.id,
              this.selectedPlan,
              this.userEmail,
              this.isLoggedIn ? undefined : this.password,
              this.isLoggedIn ? undefined : this.firstName,
              this.isLoggedIn ? undefined : this.lastName
            ).subscribe({
              next: (confirmRes: any) => {
                this.isProcessing = false;
                this.paymentSuccess = true;
                this.registeredUserName = this.firstName 
                  ? `${this.firstName} ${this.lastName || ''}`.trim() 
                  : (this.loggedInUserName || this.userEmail);
                this.securityService.updateLoginStatus();
                this.cd.detectChanges();
              },
              error: (confirmErr: any) => {
                console.error('Simulated activation error:', confirmErr);
                this.generalError = confirmErr.error?.message || confirmErr.message || 'Subscription activation error';
                this.isProcessing = false;
                this.cd.detectChanges();
              }
            });
            return;
          }

          // 3. Live Card payment with Stripe
          const stripeResult = await stripe.confirmCardPayment(clientSecret, {
            payment_method: {
              card: this.card,
              billing_details: {
                email: this.userEmail,
                name: this.firstName ? `${this.firstName} ${this.lastName || ''}`.trim() : undefined
              }
            }
          });

          if (stripeResult.error) {
            this.cardError = stripeResult.error.message || 'Payment failed with card.';
            this.isProcessing = false;
            this.cd.detectChanges();
            return;
          }

          if (stripeResult.paymentIntent && (stripeResult.paymentIntent.status === 'succeeded' || stripeResult.paymentIntent.status === 'processing')) {
            // 4. Confirm activation and create user account on backend
            this.dataService.confirmSubscription(
              stripeResult.paymentIntent.id,
              this.data.book.id,
              this.selectedPlan,
              this.userEmail,
              this.isLoggedIn ? undefined : this.password,
              this.isLoggedIn ? undefined : this.firstName,
              this.isLoggedIn ? undefined : this.lastName
            ).subscribe({
              next: (confirmRes: any) => {
                this.isProcessing = false;
                this.paymentSuccess = true;
                this.registeredUserName = this.firstName 
                  ? `${this.firstName} ${this.lastName || ''}`.trim() 
                  : (this.loggedInUserName || this.userEmail);
                this.securityService.updateLoginStatus();
                this.cd.detectChanges();
              },
              error: (confirmErr: any) => {
                console.warn('Backend confirmation error, but payment succeeded:', confirmErr);
                this.isProcessing = false;
                this.paymentSuccess = true;
                this.securityService.updateLoginStatus();
                this.cd.detectChanges();
              }
            });
          } else {
            this.generalError = 'Payment could not be completed. Status: ' + stripeResult.paymentIntent?.status;
            this.isProcessing = false;
            this.cd.detectChanges();
          }
        },
        error: (err: any) => {
          console.error('Create payment intent error:', err);
          this.generalError = err.error?.message || err.message || 'Payment service error. Please try again.';
          this.isProcessing = false;
          this.cd.detectChanges();
        }
      });
    } catch (e: any) {
      console.error('Subscription error:', e);
      this.generalError = e.message || 'An unexpected error occurred.';
      this.isProcessing = false;
      this.cd.detectChanges();
    }
  }

  closeDialog(wasPurchased: boolean = false) {
    this.dialogRef.close({ purchased: wasPurchased, plan: this.selectedPlan, bookId: this.data.book?.id });
  }
}
